// IEA Site Survey Collect screens (pass 2). Pure: state in, HTML out.
// Mockup: MOCKUP_IEA_COLLECT.html v2 (approved 2026-10-16). Words: IEA_STEP4_COLLECT_WORDS.md.
// Actions use data-cl so they never collide with data-mp (Map) or data-sv (shell).

import { esc } from "./training-screens";
import {
  blanksOf,
  lookedAt,
  cardFor,
  cardStatus,
  hasValue,
  isMccAsset,
  isSteel,
  listFor,
  nodeStatus,
  recOf,
  rowFields,
  rowRec,
  rowsOf,
  rowStatus,
  nextRowExtra,
  rowPhotoKeys,
  rowPhotoWord,
  ROW_EXTRAS,
  ROW_MAX,
  shown,
  sideFeedsInto,
  wireLinkOf,
  wireOf,
  wireRequired,
  wireSig,
  wireStatus,
  wireTag,
  type Card,
  type Field,
  type ListValue,
  type Rec,
  type Site,
  type Status,
  type Wire,
  type WorkLine,
  type WorkRoom,
} from "./survey-collect";
import { codeOf, fullId, nodeLabel, placeOf, shortName, type MapNode } from "./survey-map";
import { gearKindOf } from "./survey-gear";
import { skmName, tagsForAsset, tagsForLoad, type Prefixes, type TagRecord } from "./survey-tags";

export interface CollectPanelState {
  siteNumber: string;
  cur: string | null;
  /** The open row in a housing table: the point code. */
  row: string | null;
  tab: "device" | "wire";
  /** Housing cards the user opened after they folded. */
  openCards: ReadonlySet<string>;
  lists: readonly ListValue[];
  prefill: (assetId: string) => Record<string, string | number>;
  /** A photo's thumbnail picture (survey-photo-store.ts). */
  pic: (path: string, alt: string) => string;
  /** The cloud mark while a photo waits to upload. */
  mark: (path: string) => string;
  /** Model Tags at this site (AF_MAPPING_WORDS.md section 5). Back office: the survey is admin only. */
  tags?: readonly TagRecord[];
  prefixes?: Prefixes;
}

/** The SKM names an item carries, under its card title. Nothing when it has no tag yet. */
function tagsLine(list: readonly TagRecord[], p?: Prefixes): string {
  if (!list.length) return "";
  return `<div class="cl-tags">${list.map((t) => `<span class="mono">${esc(skmName(t, p))}</span>`).join("")}</div>`;
}

const STATUS_WORDS: Record<Status, string> = { none: "Not Started", started: "Started", missing: "Missing Data", done: "Done" };

/** The card's pill. Blue names its blanks: "Missing Data · 2 Blank" (SURVEY_DONE_LOOKED_C1_WORDS.md item 3). */
const pill = (s: Status, blanks = 0) => `<span class="cl-status ${s}">${STATUS_WORDS[s]}${s === "missing" && blanks ? ` · ${blanks} Blank` : ""}</span>`;
const dot = (s: Status) => `<span class="cl-dot ${s}"></span>`;

/** Where a field saves: an asset's record, or a typed breaker inside its housing's record. */
export const targetOf = (assetId: string, breaker?: string) => (breaker ? `B:${assetId}:${breaker}` : `A:${assetId}`);

function control(f: Field, r: Rec, target: string, lists: readonly ListValue[], prefill: string | number | undefined): string {
  // Old records marked Not Visible read as blank (item 7).
  const nv = false;
  const stored = r.f[f.name];
  const v = stored !== undefined ? String(stored) : prefill !== undefined ? String(prefill) : "";
  // Amber until looked at: a tap into the field clears it, filled or not (SURVEY_DONE_LOOKED_C1_WORDS.md item 1).
  const empty = f.req && !f.office && !lookedAt(r, f) && prefill === undefined;
  const id = `cl-${target}-${f.name}`.replace(/[^A-Za-z0-9-]/g, "_");
  let ctl: string;
  if (f.list) {
    const opts = listFor(f.list, lists);
    const extra = v && !opts.includes(v) ? [v] : [];
    ctl = `<select id="${id}" data-cl-field="${esc(f.name)}" data-cl-t="${esc(target)}"${nv ? " disabled" : ""}><option value=""></option>${[...opts, ...extra].map((o) => `<option${o === v ? " selected" : ""}>${esc(o)}</option>`).join("")}</select>`;
  } else {
    ctl = `<input id="${id}" data-cl-field="${esc(f.name)}" data-cl-t="${esc(target)}" value="${esc(nv ? "" : v)}"${nv ? " disabled" : ""} autocomplete="off"${f.num ? ' inputmode="decimal"' : ""} placeholder="${f.office ? "From the office" : "Type or shoot it"}">`;
  }
  const typical = prefill !== undefined && (stored === undefined || String(stored) === String(prefill)) && !nv ? ' <span class="cl-tag">Typical</span>' : "";
  return `<div class="cl-fld${empty ? " empty" : ""}${f.office ? " office" : ""}"><label for="${id}">${esc(f.name)}${f.office ? ' <span class="cl-tag">Office</span>' : ""}${typical}</label>
    <div class="cl-row">${ctl}</div></div>`;
}

function fieldsHtml(fields: Field[], r: Rec, target: string, s: CollectPanelState, prefill: Record<string, string | number> = {}): string {
  return `<div class="cl-fields">${fields
    .filter((f) => shown(f, r))
    .map((f) => control(f, r, target, s.lists, prefill[f.name]))
    .join("")}</div>`;
}

/** Photos on a card: real thumbnails. A tap opens the big view, never the camera.
 *  An empty one opens the camera with a big label for what the shot becomes. Take Photos walks the rest. */
function photosHtml(card: Pick<Card, "photos" | "optPhotos">, r: Rec, target: string, s: Pick<CollectPanelState, "pic" | "mark">): string {
  // Small until tapped, like the walk's photo box (SURVEY_DRAWING_IDS_C1_WORDS.md item 11). A taken one opens big.
  const one = (p: string, opt: boolean) => {
    const path = r.photos[p];
    if (path) return `<button type="button" class="cl-phmini have" data-pv="open" data-pv-t="${esc(target)}" data-pv-type="${esc(p)}" aria-label="${esc(p)} photo"><span class="cl-phmimg">${s.pic(path, p)}${s.mark(path)}</span><span class="cl-phmcap">${esc(p)}</span></button>`;
    return `<button type="button" class="cl-phmini${opt ? " opt" : " need"}" data-cl="photo" data-cl-photo="${esc(p)}" data-cl-t="${esc(target)}">${cameraIcon()}<span>${esc(p)}</span></button>`;
  };
  const extra = Object.keys(r.photos).filter((p) => !card.photos.includes(p) && !card.optPhotos.includes(p));
  return `<div class="cl-phstrip">${card.photos.map((p) => one(p, false)).join("")}${card.optPhotos.map((p) => one(p, true)).join("")}${extra.map((p) => one(p, true)).join("")}</div>`;
}

function openedHtml(r: Rec, target: string, lists: readonly ListValue[]): string {
  const reason =
    r.opened === "Not Opened"
      ? `<div class="cl-fld${r.reason ? "" : " empty"}"><label>Reason</label><div class="cl-row"><select data-cl-reason="1" data-cl-t="${esc(target)}"><option value=""></option>${listFor("Not Opened Reason", lists)
          .map((o) => `<option${o === r.reason ? " selected" : ""}>${esc(o)}</option>`)
          .join("")}</select></div></div><div class="cl-note">Not Opened stays on the work list.</div>`
      : "";
  // One compact switch, like Front | Table (Robert 2026-10-19). Blank until tapped.
  return `<div class="cl-opened"><div class="cl-sec">Opened</div><span class="sv-seg cl-seg${r.opened ? "" : " need"}"><button type="button" class="${r.opened === "Opened" ? "on" : ""}" data-cl="opened" data-cl-v="Opened" data-cl-t="${esc(target)}">Opened</button><button type="button" class="${r.opened === "Not Opened" ? "on" : ""}" data-cl="opened" data-cl-v="Not Opened" data-cl-t="${esc(target)}" aria-label="Not Opened"><span class="cl-stack"><span>Not</span><span>Opened</span></span></button></span>${reason}</div>`;
}

const isPanel = (site: Site, assetId: string) => {
  const a = site.reg.byId.get(assetId);
  return Boolean(a && gearKindOf(codeOf(a)) === "panel");
};

/** A bucket or breaker's photos as one clean row (Robert 2026-10-20). Photo 1 (the Door, or the Breaker on a
 *  panel) is needed: until it is in, the row says so and Take Photo opens the camera. After that the row shows
 *  the Door thumbnail and "Photos · 2 of 4"; a tap drops down the tiles and the next Add (none past 4).
 *  Opened sits on its own line under it. */
function photoOpened(r: Rec, t: string, s: CollectPanelState, panel: boolean): string {
  // One small line (SURVEY_DRAWING_IDS_C1_WORDS.md item 11): Add Photo until the first is in, then the photos
  // small, a tap opens one big, and + adds the next, up to 4. Photo 1 is the Door (the Breaker on a panel).
  const word = rowPhotoWord(panel);
  const keys = rowPhotoKeys(r);
  const next = r.photos.Row ? nextRowExtra(r) : null;
  const strip = !r.photos.Row
    ? `<button type="button" class="cl-phmini need" data-cl="photo" data-cl-photo="Row" data-cl-t="${esc(t)}">${cameraIcon()}<span>Add ${esc(word)} Photo</span></button>`
    : `${keys.map((k) => `<button type="button" class="cl-phmini have" data-pv="open" data-pv-t="${esc(t)}" data-pv-type="${esc(k)}" aria-label="${esc(k === "Row" ? word : k)} photo"><span class="cl-phmimg">${s.pic(r.photos[k]!, k)}${s.mark(r.photos[k]!)}</span></button>`).join("")}${
        next ? `<button type="button" class="cl-phmini add" data-cl="photo" data-cl-photo="${esc(next)}" data-cl-t="${esc(t)}" aria-label="Add ${esc(next)}">+</button>` : ""
      }${next ? `<span class="cl-phn">${keys.length}/${ROW_MAX}</span>` : ""}`;
  // One balanced line: photos left, Opened | Not Opened right (item 14). The Reason drops under it on Not Opened.
  return `<div class="cl-phop"><div class="cl-phstrip">${strip}</div>${openedHtml(r, t, s.lists)}</div>`;
}

function cameraIcon(): string {
  return '<svg width="22" height="18" viewBox="0 0 16 13" aria-hidden="true"><rect x="1" y="3" width="14" height="9" rx="2" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="7.5" r="2.6" fill="none" stroke="currentColor" stroke-width="1.4"/><rect x="5" y="0.8" width="6" height="3" rx="1" fill="currentColor"/></svg>';
}

function head(title: string, sub: string, status: Status | null, blanks = 0): string {
  return `<div class="cl-hd"><div class="cl-nm"><b>${esc(title)}</b><small>${esc(sub)}</small></div>${status ? pill(status, blanks) : ""}</div>`;
}

export function wireButton(site: Site, linkId: string, required: boolean, label = "Wire In"): string {
  const w = wireOf(site, linkId);
  const st = wireStatus(w);
  return `<div class="cl-sec">${esc(label)}</div><button type="button" class="cl-fold1" data-cl="wire" data-cl-link="${esc(linkId)}">${dot(st)}<b class="mono">${esc(wireTag(w))}</b><span>${required ? (st === "done" ? "Tap to change" : "Tap to fill") : "Optional on a load"}</span></button>`;
}

function wiresIn(site: Site, n: MapNode): string {
  const lid = wireLinkOf(n);
  let h = lid ? wireButton(site, lid, wireRequired(n)) : "";
  if (n.kind === "asset") for (const l of sideFeedsInto(site, n.ref)) h += wireButton(site, l.id, true, `Wire In · Second Source from ${l.fromAsset}`);
  return h;
}

/** The left panel in Collect: the card for whatever is selected. */
export function collectPanel(site: Site, s: CollectPanelState): string {
  const n = s.cur ? site.tree.nodes.get(s.cur) : undefined;
  if (!n) return '<div class="cl-note">Tap any node on the diagram, or open the Work List.</div>';
  if (n.kind === "room") return `${head(n.ref, "Electrical room", null)}<div class="cl-note">A room has nothing to collect. Tap an asset in it.</div>`;
  if (n.kind === "load") {
    const l = site.links.find((x) => x.id === n.ref);
    const motor = l?.endLoadType !== "Non-motor";
    const hp = l?.endLoadHp ?? "";
    return `${head(nodeLabel(site.reg, n, site.links), "Load · no QR", null)}${tagsLine(tagsForLoad(s.tags ?? [], n.ref), s.prefixes)}
      <div class="cl-note">${l?.notes ? `"${esc(l.notes)}" as found. ` : ""}Every motor stays on the diagram for LOTO. Study Export adds the small ones together for the engineer.</div>
      <div class="cl-fields"><div class="cl-fld"><label>Type</label><div class="cl-row"><input value="${motor ? "Motor" : "Other Load"}" disabled></div></div>
      <div class="cl-fld"><label for="cl-loadhp">HP</label><div class="cl-row"><input id="cl-loadhp" data-cl-loadhp="${esc(n.ref)}" inputmode="decimal" value="${esc(hp)}"></div></div></div>
      ${wiresIn(site, n)}`;
  }
  if (n.kind === "point") return housingCard(site, s, n.asset, n.ref);
  const card = cardFor(site, n.ref);
  if (card.kind === "housing") return housingCard(site, s, n.ref, s.row);
  const a = site.reg.byId.get(n.ref);
  const r = recOf(site, n.ref);
  const target = targetOf(n.ref);
  return `${head(a ? shortName(a) : n.ref, `${fullId(s.siteNumber, n)} · ${a?.equipmentType || card.title}${a && placeOf(a).room ? ` · ${placeOf(a).room}` : ""}`, cardStatus(card, r), blanksOf(card.fields, r))}${tagsLine(tagsForAsset(s.tags ?? [], n.ref), s.prefixes)}
    <div class="cl-sec">Photos</div>${photosHtml(card, r, target, s)}
    <div class="cl-sec">${esc(card.title)}</div>${fieldsHtml(card.fields, r, target, s, s.prefill(n.ref))}
    ${card.opened ? openedHtml(r, target, s.lists) : ""}
    ${wiresIn(site, n)}`;
}

/**
 * The card on the survey's room cards (SURVEY_CARDS_C1_WORDS.md items 6 to 9): the same fields, photos and Opened
 * as Collect on the diagram. No door button and no Wire In: the drawing sits beside it and Fed From and Wire sit on
 * the card's top line. Works for gear not on the Map yet.
 */
export function cardBody(site: Site, s: CollectPanelState, assetId: string): string {
  const card = cardFor(site, assetId);
  if (card.kind === "housing") return housingCard(site, s, assetId, null, true).replace(/^<div class="cl-hd">[\s\S]*?<\/div>(<span class="cl-status[^>]*>[^<]*<\/span>)?<\/div>/, "");
  const r = recOf(site, assetId);
  const target = targetOf(assetId);
  return `${tagsLine(tagsForAsset(s.tags ?? [], assetId), s.prefixes)}
    <div class="cl-sec">Photos</div>${photosHtml(card, r, target, s)}
    <div class="cl-sec">${esc(card.title)}</div>${fieldsHtml(card.fields, r, target, s, s.prefill(assetId))}
    ${card.opened ? openedHtml(r, target, s.lists) : ""}`;
}

function housingCard(site: Site, s: CollectPanelState, assetId: string, rowPoint: string | null, onCards = false): string {
  const a = site.reg.byId.get(assetId);
  const card = cardFor(site, assetId);
  const r = recOf(site, assetId);
  const target = targetOf(assetId);
  const node = site.tree.nodes.get(`a:${assetId}`);
  const cs = cardStatus(card, r);
  const status = node ? nodeStatus(site, node) : cs;
  let h = head(a ? shortName(a) : assetId, `${s.siteNumber}-${assetId.replace(/^\d{2}-/, "")} · ${a?.equipmentType || "Housing"}`, status) + tagsLine(tagsForAsset(s.tags ?? [], assetId), s.prefixes);
  if (cs === "done" && !s.openCards.has(assetId)) {
    h += `<button type="button" class="cl-fold1" data-cl="open-card" data-cl-a="${esc(assetId)}">${dot("done")}<b>Housing Card</b><span>Photos and fields done · tap to open</span></button>`;
  } else {
    h += `<div class="cl-sec">Photos</div>${photosHtml(card, r, target, s)}<div class="cl-sec">Housing</div>${fieldsHtml(card.fields, r, target, s, s.prefill(assetId))}${openedHtml(r, target, s.lists)}`;
  }
  if (onCards) return h;
  if (node) h += wiresIn(site, node);
  // An MCC opens its door: the gear front, with the table as a tab (IEA_GEAR_VIEW_WORDS.md section 7 step 1).
  const kind = a ? gearKindOf(codeOf(a)) : null;
  if (kind) {
    const n = rowsOf(site, assetId).length;
    const word = kind === "mcc" ? "Buckets" : "Breakers";
    const what = kind === "mcc" ? "the lineup" : kind === "panel" ? "the panel" : "the switchboard";
    h += `<div class="cl-sec">${word} · ${n}</div><button type="button" class="sv-btn gold gv-openbtn" data-gv="open" data-gv-a="${esc(assetId)}">Open Gear Front</button><div class="cl-note">The front of ${what}, drawn like the gear. Tap a ${kind === "mcc" ? "bucket" : "breaker"} to fill its card. The table is a tab inside.</div>`;
    return h;
  }
  h += tableHtml(site, s, assetId);
  if (rowPoint) h += rowCard(site, s, assetId, rowPoint);
  return h;
}

export function tableHtml(site: Site, s: CollectPanelState, assetId: string): string {
  const rows = rowsOf(site, assetId);
  const mcc = isMccAsset(site, assetId);
  if (!rows.length) return '<div class="cl-note">No positions or breakers yet. Add breaker numbers on the Map.</div>';
  const devList = listFor("Device Type", s.lists);
  const starters = listFor("Starter Type", s.lists);
  let h = `<div class="cl-sec">${mcc ? "Buckets" : "Breakers"} · ${rows.length} rows</div>
    <div class="cl-tabs"><button type="button" class="cl-t${s.tab === "device" ? " on" : ""}" data-cl="tab" data-cl-v="device">Device</button><button type="button" class="cl-t${s.tab === "wire" ? " on" : ""}" data-cl="tab" data-cl-v="wire">Wire</button><span class="cl-grow"></span>${s.tab === "device" ? `<button type="button" class="sv-btn gold sm" data-cl="photo-run" data-cl-a="${esc(assetId)}">Photo Run</button>` : ""}</div>
    <div class="cl-tbl"><table><thead><tr><th></th><th>Pos</th>${s.tab === "device" ? `<th>Photo</th><th>Opened</th><th>Device</th><th>Rating</th><th>Setting</th>${mcc ? "<th>Starter</th>" : ""}<th></th>` : "<th>Feeds</th><th>Wire Out</th>"}</tr></thead><tbody>`;
  rows.forEach((row, i) => {
    const r = rowRec(site, assetId, row);
    const st = rowStatus(r, mcc);
    const t = row.assetId ? targetOf(row.assetId) : targetOf(assetId, row.point);
    const no = r.opened === "Not Opened";
    const cur = s.row === row.point;
    h += `<tr class="${cur ? "cur " : ""}${no ? "notopen" : ""}" id="cl-row-${esc(row.point)}"><td>${dot(st)}</td><td class="cl-pos"><button type="button" data-cl="row" data-cl-v="${esc(row.point)}">${esc(row.point)}</button></td>`;
    if (s.tab === "device") {
      // A word typed under Other shows as its own choice until it is added on the Lists page.
      const sel = (field: string, list: string[], v: string) => `<select data-cl-field="${esc(field)}" data-cl-t="${esc(t)}" class="${v || r.looked[field] ? "" : "empty"}"><option value=""></option>${[...list, ...(v && !list.includes(v) ? [v] : [])].map((o) => `<option${o === v ? " selected" : ""}>${esc(o)}</option>`).join("")}</select>`;
      const rp = r.photos.Row;
      h += `<td>${no ? "" : rp ? `<button type="button" class="cl-shot got" data-pv="open" data-pv-t="${esc(t)}" data-pv-type="Row" aria-label="${esc(row.point)} photo">${s.pic(rp, row.point)}${s.mark(rp)}</button>` : `<button type="button" class="cl-shot" data-cl="photo" data-cl-photo="Row" data-cl-t="${esc(t)}">Shoot</button>`}</td>`;
      h += `<td><select data-cl-opened="1" data-cl-t="${esc(t)}" class="${r.opened ? "" : "empty"}"><option value=""></option><option${r.opened === "Opened" ? " selected" : ""}>Opened</option><option${no ? " selected" : ""}>Not Opened</option></select></td>`;
      if (no) {
        h += `<td colspan="${mcc ? 4 : 3}"><select data-cl-reason="1" data-cl-t="${esc(t)}" class="${r.reason ? "" : "empty"}"><option value="">Reason</option>${listFor("Not Opened Reason", s.lists)
          .map((o) => `<option${o === r.reason ? " selected" : ""}>${esc(o)}</option>`)
          .join("")}</select> <span class="cl-small">Stays on the work list</span></td><td></td>`;
      } else {
        const v = (k: string) => (r.f[k] !== undefined ? String(r.f[k]) : "");
        h += `<td>${sel("Device Type", devList, v("Device Type"))}</td>`;
        h += `<td><input class="cl-num${v("Rating (A)") || r.looked["Rating (A)"] ? "" : " empty"}" data-cl-field="Rating (A)" data-cl-t="${esc(t)}" inputmode="decimal" value="${esc(v("Rating (A)"))}" placeholder="A"></td>`;
        h += `<td><input class="cl-set" data-cl-field="Setting" data-cl-t="${esc(t)}" value="${esc(v("Setting"))}" placeholder="as read"></td>`;
        if (mcc) h += `<td>${sel("Starter Type", starters, v("Starter Type"))}</td>`;
        h += `<td>${i > 0 ? `<button type="button" class="cl-same" data-cl="same" data-cl-a="${esc(assetId)}" data-cl-v="${i}">Same</button>` : ""}</td>`;
      }
    } else {
      const below = feedBelow(site, assetId, row.point);
      const side = row.assetId ? sideFeedsInto(site, assetId, row.point)[0] : undefined;
      const lid = below ? wireLinkOf(below) : null;
      h += `<td>${below ? esc(nodeLabel(site.reg, below, site.links)) : side ? `Fed separately from ${esc(side.fromAsset)}` : '<span class="cl-small">Not mapped</span>'}</td><td>`;
      if (lid) h += `<button type="button" class="cl-same" data-cl="wire" data-cl-link="${esc(lid)}">${esc(wireTag(wireOf(site, lid)))}${below && !wireRequired(below) ? " · optional" : ""}</button>`;
      if (side) h += `<button type="button" class="cl-same" data-cl="wire" data-cl-link="${esc(side.id)}">${esc(wireTag(wireOf(site, side.id)))}</button>`;
      h += "</td>";
    }
    h += "</tr>";
  });
  return `${h}</tbody></table></div><div class="cl-note">Tap a position to open its full breaker or fuse fields. Same copies the row above, all but the photo.</div>`;
}

/** The main inside the gear front: the housing's Main or MLO and its main device fields. */
export function mainCard(site: Site, s: CollectPanelState, assetId: string): string {
  const card = cardFor(site, assetId);
  const r = recOf(site, assetId);
  const fields = card.fields.filter((f) => f.name.startsWith("Main"));
  return `<div class="cl-note">Saved on the housing card. Main or MLO decides what else it asks.</div>${fieldsHtml(fields, r, targetOf(assetId), s)}`;
}

/** What a position feeds on the map. Gear in another room sits under a room node, so step through it. */
export function feedBelow(site: Pick<Site, "tree">, assetId: string, point: string): MapNode | undefined {
  let key: string | undefined = `p:${assetId}:${point}`;
  for (let depth = 0; key && depth < 3; depth++) {
    const kids: MapNode[] = (site.tree.kids.get(key) ?? []).map((k) => site.tree.nodes.get(k)!).filter(Boolean);
    const hit = kids.find((x) => x.kind === "asset" || x.kind === "load");
    if (hit) return hit;
    key = kids.find((x) => x.kind === "room")?.key;
  }
  return undefined;
}

/** The PD tag of a bucket or breaker: its own QR's ID, or the dot ID for a breaker inside the panel. */
function positionTags(s: CollectPanelState, assetId: string, point: string, rowAssetId: string | null): string {
  const id = rowAssetId ? `${s.siteNumber}-${rowAssetId.replace(/^\d{2}-/, "")}` : `${assetId}.${point}`;
  return tagsLine(tagsForAsset(s.tags ?? [], id), s.prefixes);
}

/** A bucket's card inside the gear front: photo, Opened, device fields, the wire out. */
export function bucketCard(site: Site, s: CollectPanelState, assetId: string, point: string): string {
  const row = rowsOf(site, assetId).find((r) => r.point === point);
  if (!row) return '<div class="cl-note">This position is not in the registry or on the map.</div>';
  const r = rowRec(site, assetId, row);
  const t = row.assetId ? targetOf(row.assetId) : targetOf(assetId, row.point);
  const mcc = isMccAsset(site, assetId);
  const below = feedBelow(site, assetId, point);
  const lid = below ? wireLinkOf(below) : null;
  return `<div class="cl-rowcard" id="cl-rowcard"><div class="cl-hd"><div class="cl-nm"><small>${esc(row.assetId ? `${s.siteNumber}-${row.assetId.replace(/^\d{2}-/, "")}` : `Breaker ${point} · stored on the panel`)}</small></div>${pill(rowStatus(r, mcc), blanksOf(rowFields(mcc), r))}</div>${positionTags(s, assetId, point, row.assetId)}
    ${photoOpened(r, t, s, isPanel(site, assetId))}
    ${r.opened === "Not Opened" ? "" : `<div class="cl-sec">Device</div>${fieldsHtml(rowFields(mcc), r, t, s)}`}
    ${lid && below ? wireButton(site, lid, wireRequired(below), "Wire Out") : ""}</div>`;
}

function rowCard(site: Site, s: CollectPanelState, assetId: string, point: string): string {
  const row = rowsOf(site, assetId).find((r) => r.point === point);
  if (!row) return "";
  const r = rowRec(site, assetId, row);
  const t = row.assetId ? targetOf(row.assetId) : targetOf(assetId, row.point);
  const mcc = isMccAsset(site, assetId);
  return `<div class="cl-rowcard" id="cl-rowcard"><div class="cl-hd"><div class="cl-nm"><b>${esc(point)}</b><small>${esc(row.assetId ? `${s.siteNumber}-${row.assetId.replace(/^\d{2}-/, "")}` : `Breaker ${point} · stored on the panel`)}</small></div>${pill(rowStatus(r, mcc), blanksOf(rowFields(mcc), r))}<button type="button" class="sv-btn line sm" data-cl="row-close">Back To Table</button></div>${positionTags(s, assetId, point, row.assetId)}
    ${photoOpened(r, t, s, isPanel(site, assetId))}
    ${r.opened === "Not Opened" ? "" : `<div class="cl-sec">Device</div>${fieldsHtml(rowFields(mcc), r, t, s)}`}</div>`;
}

// ---- The wire card ----

export function wireSheet(site: Site, linkId: string, required: boolean, lists: readonly ListValue[], picks: readonly Wire[], removable = false): string {
  const w = wireOf(site, linkId);
  const l = site.links.find((x) => x.id === linkId);
  const from = l ? `${l.fromAsset.replace(/^\d{2}-/, "")} ${l.fromPoint}` : "";
  const to = l?.toAsset ? l.toAsset.replace(/^\d{2}-/, "") : "the load";
  const sel = (field: keyof Wire, list: string, label: string) => {
    const v = String(w[field] ?? "");
    const opts = listFor(list, lists);
    return `<div class="cl-fld${v || !required ? "" : " empty"}"><label>${esc(label)}</label><div class="cl-row"><select data-cl-wire="${field}"><option value=""></option>${[...opts, ...(v && !opts.includes(v) ? [v] : [])].map((o) => `<option${o === v ? " selected" : ""}>${esc(o)}</option>`).join("")}</select></div></div>`;
  };
  const num = (field: "length" | "amps", label: string) => `<div class="cl-fld${w[field] !== "" || !required ? "" : " empty"}"><label>${esc(label)}</label><div class="cl-row"><input data-cl-wire="${field}" inputmode="decimal" value="${esc(w[field])}" autocomplete="off"></div></div>`;
  const busway = w.type === "Busway";
  const steel = isSteel(w.raceway);
  return `<div class="cl-sheet" data-cl="sheet-bg"><div class="cl-card">
    <div class="cl-sheethd"><b>Wire into ${esc(to)}<small>From ${esc(from)} · ${esc(wireTag(w))}</small></b><button class="sv-btn line sm" type="button" data-cl="sheet-close">Close</button></div>
    <div class="cl-sheetbody">
      <div class="cl-fld"><label>Recent Wires · wires already entered at this site</label><div class="cl-row"><select data-cl-quick="1"><option value="">${picks.length ? "Pick a wire you already entered" : "None yet. Fill the first one below."}</option>${picks.map((p, i) => `<option value="${i}">${esc(wireSig(p))}</option>`).join("")}</select></div></div>
      <div class="cl-picks"><button type="button" class="cl-pick${busway ? "" : " on"}" data-cl="wire-type" data-cl-v="Cable">Cable</button><button type="button" class="cl-pick${busway ? " on" : ""}" data-cl="wire-type" data-cl-v="Busway">Busway</button><span class="cl-grow"></span><button type="button" class="sv-btn line sm" data-cl="wire-last">Same As Last Wire</button></div>
      <div class="cl-fields">${busway ? `${num("amps", "Amp Rating")}${sel("material", "Conductor Material", "Material")}` : `${sel("size", "Conductor Size", "Size (AWG / kcmil)")}${sel("sets", "Conductors per Phase", "Sets (per phase)")}${sel("material", "Conductor Material", "Material")}${sel("insulation", "Insulation Type", "Insulation")}${sel("raceway", "Raceway Type", "Raceway")}`}${num("length", "Length (ft)")}</div>
      <div class="cl-note">${required ? "Phase conductors only. The neutral and ground do not change three-phase fault current." : "Optional on a load. The engineer often leaves the last run to a motor out. It never holds the work list."}${steel === null ? "" : ` ${esc(w.raceway)} counts as ${steel ? "steel" : "non-steel"} for the model.`} Other lets you type a size or type. To keep it for good, add it on the Lists page.</div>
      <div class="sv-btns">${removable ? `<button type="button" class="sv-btn line" data-dw="remove-line" data-dw-v="${esc(linkId)}">Remove Line</button>` : ""}<button type="button" class="sv-btn gold" data-cl="sheet-close">Save Wire</button></div>
    </div></div></div>`;
}

export function otherSheet(title: string, field: string): string {
  return `<div class="cl-sheet" data-cl="sheet-bg"><form class="cl-card cl-otherform" data-cl-form="other"><div class="cl-sheethd"><b>${esc(title)}<small>Typed in the field. Add it on the Lists page to keep it for every site.</small></b><button class="sv-btn line sm" type="button" data-cl="sheet-close">Cancel</button></div>
    <div class="cl-sheetbody"><div class="cl-fld"><label for="cl-other">${esc(field)}</label><div class="cl-row"><input id="cl-other" autocomplete="off" autocapitalize="characters"></div></div><div class="sv-btns"><button type="submit" class="sv-btn gold">Use This</button></div></div></form></div>`;
}

// ---- The work list ----

export function workListSheet(siteName: string, rooms: WorkRoom[], totals: { nodes: number; wires: number; photos: number }): string {
  // Blue lines go to one Missing Data group after the rooms, the hotel list (SURVEY_DONE_LOOKED_C1_WORDS.md item 6).
  const blue = (l: WorkLine) => l.status === "missing" && !l.rowsLeft && !l.wireLeft;
  const line = (l: WorkLine) =>
    `<button type="button" class="cl-wlrow" data-cl="jump" data-cl-k="${esc(l.key)}">${dot(l.status)}<b>${esc(l.label)}</b><span>${STATUS_WORDS[l.status]}${l.rowsLeft ? ` · ${l.rowsLeft} rows left` : ""}${l.notOpened ? ` · ${l.notOpened} not opened` : ""}${l.wireLeft ? " · wire in" : ""}</span></button>`;
  const open = rooms
    .map((r) => ({ room: r.room, lines: r.lines.filter((l) => !blue(l)) }))
    .filter((r) => r.lines.length)
    .map((r) => `<div class="cl-wlroom">${esc(r.room)}</div>${r.lines.map(line).join("")}`)
    .join("");
  const missing = rooms.flatMap((r) => r.lines.filter(blue));
  const body = open + (missing.length ? `<div class="cl-wlroom miss">Missing Data · ${missing.length}</div>${missing.map(line).join("")}` : "");
  return `<div class="cl-sheet" data-cl="sheet-bg"><div class="cl-card"><div class="cl-sheethd"><b>Work List · ${esc(siteName)}<small>Left: ${totals.nodes} nodes · ${totals.wires} wires · ${totals.photos} photos. Built from the map. Tap a line to jump to it.</small></b><button class="sv-btn line sm" type="button" data-cl="sheet-close">Close</button></div><div class="cl-sheetbody">${body || '<div class="cl-note">Nothing mapped yet. Map the site first.</div>'}</div></div></div>`;
}
